import { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { userRequest } from "../../requestMethods";
import { useCompany } from "../../components/context/CompanyContext";
import InstagramConnect from "../../components/instagram/InstagramConnect";
import "../../global.css";

// Mesmo padrão do app (mobileNextJs/src/util/companyConfig.ts).
const DEFAULT_RESELLER_LABEL = "Exclusivo para revendedor(a)!";

// Tipos de pedido que o vendedor pode criar (configRules.typeOrder).
// Mesmos valores de api/service/orderType.helpers.js.
const TYPE_ORDER_OPTIONS = ["Pronta Entrega", "Programado", "Ambas"];
const DEFAULT_TYPE_ORDER = "Pronta Entrega";

// section = "approval" | "marketing"
export default function System({ section }) {
  const { companyId } = useCompany();
  const currentUser = useSelector((state) => state.user.currentUser);

  // --- Aprovação de Clientes ---
  const [approveAll, setApproveAll] = useState(false);
  const [approvalActivities, setApprovalActivities] = useState("");

  // --- Marketing Digital ---
  const inputRefPixel = useRef("");
  const inputRefGmt = useRef("");

  // --- Integração com sistema legado ---
  const [externalIntegration, setExternalIntegration] = useState(false);
  const [statusIntegration, setStatusIntegration] = useState("Finalizado");
  const [enabledPayment, setEnabledPayment] = useState(false);
  const [statusIntegrationPaid, setStatusIntegrationPaid] = useState("Pago");

  // --- Regras de exibição (configRules) ---
  const [configRules, setConfigRules] = useState({});
  const [showPrice, setShowPrice] = useState(false);
  const [delivery, setDelivery] = useState(false);
  const [sharePriceOpen, setSharePriceOpen] = useState(false);
  // "loja" = vendedor vê só a grade da sua loja (padrão); "global" = vê toda a grade
  const [stock, setStock] = useState("loja");
  // Login do cliente exige código de verificação enviado pelo WhatsApp do vendedor
  const [codeLogin, setCodeLogin] = useState(false);
  // Catálogo só aparece para quem está logado (visitante anônimo vai pro login)
  const [catalogLoginRequired, setCatalogLoginRequired] = useState(false);
  // Tipo de pedido: "Pronta Entrega" (padrão) | "Programado" | "Ambas"
  const [typeOrder, setTypeOrder] = useState(DEFAULT_TYPE_ORDER);

  // --- Config de UI (configUI) ---
  // Guardamos o configUI inteiro para mesclar no save (a coluna é substituída
  // por completo no update; enviar só um campo apagaria logo/layout/etc.).
  const [configUI, setConfigUI] = useState({});
  // Grade com quantidade somente leitura (só +/− no app). Ausente = ligado.
  const [readonlyInputGrid, setReadonlyInputGrid] = useState(true);
  // Frase da tela de login do app. Vazio = app usa o texto padrão.
  const [resellerLabel, setResellerLabel] = useState("");

  const ORDER_STATUSES = ["Pendente", "Confirmado", "Pago", "Finalizado", "Cancelado"];

  useEffect(() => {
    if (companyId > 0) fetchCompany();
  }, [companyId]);

  const fetchCompany = async () => {
    try {
      const res = await userRequest.get(`${companyId}/company`);
      const c = res.data;
      if (c.customerApprovel) {
        setApproveAll(c.customerApprovel.approval === true);
        const acts = Array.isArray(c.customerApprovel.mainActivityId)
          ? c.customerApprovel.mainActivityId
          : [];
        setApprovalActivities(acts.join(", "));
      }
      if (c.digitalMkt) {
        if (inputRefPixel.current) inputRefPixel.current.value = c.digitalMkt.pixel || "";
        if (inputRefGmt.current)   inputRefGmt.current.value   = c.digitalMkt.gmt   || "";
      }
      setExternalIntegration(c.externalIntegration === true);
      setStatusIntegration(c.statusIntegration || "Finalizado");
      setEnabledPayment(c.enabledPayment === true);
      setStatusIntegrationPaid(c.statusIntegrationPaid || "Pago");
      if (c.configRules) {
        setConfigRules(c.configRules);
        setShowPrice(!!(c.configRules.showPrice ?? c.planContract?.showPrice));
        setDelivery(!!(c.configRules.delivery ?? c.planContract?.delivery));
        setSharePriceOpen(!!c.configRules.sharePriceOpen);
        setStock(c.configRules.stock === "global" ? "global" : "loja");
        setCodeLogin(
          c.configRules.codeLogin === true || c.configRules.codeLogin === "true",
        );
        setCatalogLoginRequired(
          c.configRules.catalogLoginRequired === true ||
            c.configRules.catalogLoginRequired === "true",
        );
        setTypeOrder(
          TYPE_ORDER_OPTIONS.includes(c.configRules.typeOrder)
            ? c.configRules.typeOrder
            : DEFAULT_TYPE_ORDER,
        );
      }
      if (c.configUI) {
        setConfigUI(c.configUI);
        // Só desliga quando explicitamente false; ausente mantém o padrão (ligado).
        setReadonlyInputGrid(c.configUI.readonlyInputGrid !== false);
        setResellerLabel(c.configUI.resellerLabel ?? "");
      }
    } catch {}
  };

  const save = async () => {
    try {
      const approvalBody = {
        customerApprovel: {
          approval: approveAll,
          mainActivityId: approveAll
            ? []
            : approvalActivities
                .split(",")
                .map((s) => s.trim())
                .filter((s) => s !== "")
                .map((s) => Number(s.replace(/\D/g, "")))
                .filter((n) => !isNaN(n)),
        },
      };
      const marketingBody = {
        digitalMkt: {
          pixel: inputRefPixel.current?.value ?? "",
          gmt:   inputRefGmt.current?.value   ?? "",
        },
      };
      const integrationBody = {
        externalIntegration,
        statusIntegration: externalIntegration ? statusIntegration : null,
        ...(enabledPayment && { statusIntegrationPaid }),
      };
      const configRulesBody = {
        configRules: {
          ...(configRules ?? {}),
          showPrice,
          delivery,
          sharePriceOpen,
          stock,
          codeLogin,
          catalogLoginRequired,
          typeOrder,
        },
      };
      const configUIBody = {
        configUI: {
          ...(configUI ?? {}),
          readonlyInputGrid,
          resellerLabel: resellerLabel.trim(),
        },
      };
      const body =
        section === "approval" ? approvalBody
        : section === "marketing" ? marketingBody
        : { ...approvalBody, ...marketingBody, ...integrationBody, ...configRulesBody, ...configUIBody };

      const res = await userRequest.put(`${companyId}/company/`, body);
      if (Object.keys(res.data).length > 0) {
        Swal.fire("Parabéns!", "Configurações salvas com sucesso!", "success");
      } else {
        Swal.fire("Atenção!", "Erro ao salvar!", "error");
      }
    } catch {}
  };

  return (
    <div className="cust-page">
      {(section === "approval" || section === "geral") && (
        <>
          <div className="cust-title">Aprovação de Clientes</div>
          <div className="cust-card">
            <div className="cust-fields">
              <div className="cust-field">
                <label
                  className="cust-label cust-checkbox-label"
                  style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={approveAll}
                    onChange={(e) => setApproveAll(e.target.checked)}
                    style={{ width: "20px", height: "20px", cursor: "pointer", margin: 0 }}
                  />
                  <span>Aprova todos os clientes automaticamente?</span>
                </label>
              </div>
              {!approveAll && (
                <div className="cust-field">
                  <label className="cust-label">
                    Atividades (CNAE) que aprovam automaticamente
                  </label>
                  <input
                    type="text"
                    className="cust-input"
                    placeholder="Ex.: 4781400, 4751201"
                    value={approvalActivities}
                    onChange={(e) => setApprovalActivities(e.target.value)}
                  />
                  <small style={{ color: "#888" }}>
                    Separe por vírgula. Clientes com essas atividades serão aprovados;
                    os demais ficam pendentes.
                  </small>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {(section === "marketing" || section === "geral") && (
        <>
          <div className="cust-title">Marketing Digital</div>
          <div className="cust-card">
            <div className="cust-fields cust-fields-3">
              <div className="cust-field">
                <label className="cust-label">Pixel</label>
                <input
                  type="text"
                  name="pixel"
                  className="cust-input"
                  ref={inputRefPixel}
                  maxLength="50"
                />
              </div>
              <div className="cust-field">
                <label className="cust-label">Google Tag Manager</label>
                <input
                  type="text"
                  name="gmt"
                  className="cust-input"
                  ref={inputRefGmt}
                  maxLength="15"
                />
              </div>
            </div>
          </div>
        </>
      )}

      {section === "geral" && (
        <>
          <div className="cust-title">Regras de Exibição</div>
          <div className="cust-card">
            <div className="cust-fields cust-fields-3">
              <div className="cust-field">
                <label className="cust-label">Compartilhar com preço aberto</label>
                <select
                  className="cust-input"
                  value={sharePriceOpen ? "true" : "false"}
                  onChange={(e) => setSharePriceOpen(e.target.value === "true")}
                >
                  <option value="false">Desativado</option>
                  <option value="true">Ativado</option>
                </select>
              </div>
              <div className="cust-field">
                <label className="cust-label">Exibir preço</label>
                <select
                  className="cust-input"
                  value={showPrice ? "true" : "false"}
                  onChange={(e) => setShowPrice(e.target.value === "true")}
                >
                  <option value="false">Desativado</option>
                  <option value="true">Ativado</option>
                </select>
              </div>
              <div className="cust-field">
                <label className="cust-label">Entrega (frete)</label>
                <select
                  className="cust-input"
                  value={delivery ? "true" : "false"}
                  onChange={(e) => setDelivery(e.target.value === "true")}
                >
                  <option value="false">Desativado</option>
                  <option value="true">Ativado</option>
                </select>
              </div>
              <div className="cust-field">
                <label className="cust-label">Estoque do vendedor de loja</label>
                <select
                  className="cust-input"
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                >
                  <option value="loja">Apenas da sua loja</option>
                  <option value="global">Toda a grade (global)</option>
                </select>
                <small style={{ color: "#888" }}>
                  "Apenas da sua loja" mantém o pedido vinculado à loja do vendedor.
                  "Global" libera o vendedor a ver toda a grade.
                </small>
              </div>
              <div className="cust-field">
                <label className="cust-label">Login com código de verificação</label>
                <select
                  className="cust-input"
                  value={codeLogin ? "true" : "false"}
                  onChange={(e) => setCodeLogin(e.target.value === "true")}
                >
                  <option value="false">Desativado</option>
                  <option value="true">Ativado</option>
                </select>
                <small style={{ color: "#888" }}>
                  "Ativado" exige que o cliente digite um código de 6 dígitos no login.
                  O código é enviado no WhatsApp do cliente pelo número do vendedor
                  responsável por ele.
                </small>
              </div>
              <div className="cust-field">
                <label className="cust-label">Catálogo apenas para clientes logados</label>
                <select
                  className="cust-input"
                  value={catalogLoginRequired ? "true" : "false"}
                  onChange={(e) => setCatalogLoginRequired(e.target.value === "true")}
                >
                  <option value="false">Desativado</option>
                  <option value="true">Ativado</option>
                </select>
                <small style={{ color: "#888" }}>
                  "Ativado" esconde os produtos de quem não está logado: o visitante
                  é levado para a tela de login e volta ao catálogo depois de entrar.
                </small>
              </div>
              <div className="cust-field">
                <label className="cust-label">Quantidade da grade somente leitura</label>
                <select
                  className="cust-input"
                  value={readonlyInputGrid ? "true" : "false"}
                  onChange={(e) => setReadonlyInputGrid(e.target.value === "true")}
                >
                  <option value="true">Ativado</option>
                  <option value="false">Desativado</option>
                </select>
                <small style={{ color: "#888" }}>
                  "Ativado" só permite alterar a quantidade pelos botões +/− na grade.
                  "Desativado" permite digitar a quantidade direto no campo.
                </small>
              </div>
            </div>
          </div>

          <div className="cust-title">Tipos de Pedido</div>
          <div className="cust-card">
            <div className="cust-fields cust-fields-3">
              <div className="cust-field">
                <label className="cust-label">Tipo de pedido</label>
                <select
                  className="cust-input"
                  value={typeOrder}
                  onChange={(e) => setTypeOrder(e.target.value)}
                >
                  <option value="Pronta Entrega">Pronta Entrega</option>
                  <option value="Programado">Pedido Programado</option>
                  <option value="Ambas">Ambas</option>
                </select>
                <small style={{ color: "#888" }}>
                  Define o tipo gravado nos pedidos. "Ambas" mostra no detalhe do
                  cliente os botões "Criar Pedido" (pronta entrega) e "Criar Pedido
                  Programado". Padrão: Pronta Entrega.
                </small>
              </div>
            </div>
          </div>

          <div className="cust-title">Tela de Login</div>
          <div className="cust-card">
            <div className="cust-fields">
              <div className="cust-field">
                <label className="cust-label">Frase de destaque</label>
                <input
                  type="text"
                  className="cust-input"
                  placeholder={DEFAULT_RESELLER_LABEL}
                  value={resellerLabel}
                  onChange={(e) => setResellerLabel(e.target.value)}
                  maxLength="100"
                />
                <small style={{ color: "#888" }}>
                  Exibida abaixo do logo na tela de login do app. Em branco, usa o
                  padrão "{DEFAULT_RESELLER_LABEL}".
                </small>
              </div>
            </div>
          </div>

          <div className="cust-title">Integração com Sistema Legado</div>
          <div className="cust-card">
            <div className="cust-fields">
              <div className="cust-field">
                <label className="cust-label">Status do pedido para envio</label>
                <select
                  className="cust-input"
                  value={statusIntegration}
                  onChange={(e) => setStatusIntegration(e.target.value)}
                  disabled={!externalIntegration}
                >
                  {ORDER_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <small style={{ color: externalIntegration ? "#888" : "#ccc" }}>
                  Ao atingir este status, o pedido é enviado ao sistema legado.
                  Padrão: Finalizado.
                </small>
              </div>

              {enabledPayment && (
                <div className="cust-field">
                  <label className="cust-label">
                    Status do pedido para envio (Pagamento)
                  </label>
                  <select
                    className="cust-input"
                    value={statusIntegrationPaid}
                    onChange={(e) => setStatusIntegrationPaid(e.target.value)}
                    disabled={!externalIntegration}
                  >
                    {ORDER_STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <small style={{ color: externalIntegration ? "#888" : "#ccc" }}>
                    Status que dispara o envio ao legado quando o pedido foi
                    <b> pago pelo app</b>. Padrão: Pago.
                  </small>
                </div>
              )}
            </div>
          </div>

          <InstagramConnect
            companyId={companyId}
            isAdmin={currentUser && currentUser.isAdmin}
          />
        </>
      )}

      <div className="cust-footer">
        <Button
          variant="contained"
          color="secondary"
          startIcon={<SaveIcon />}
          onClick={save}
          size="small"
        >
          Salvar
        </Button>
      </div>
    </div>
  );
}
